const TOKEN_KEY = "denkbroker_admin_token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getToken();
  const response = await fetch(path, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  });

  if (!response.ok) {
    if (response.status === 401) clearToken();
    const body = await response.json().catch(() => ({ detail: response.statusText }));
    throw new ApiError(response.status, body.detail ?? response.statusText);
  }

  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export type MachineStatus = "active" | "revoked";

export interface Machine {
  id: string;
  name: string;
  cert_fingerprint: string | null;
  cert_subject: string | null;
  status: MachineStatus;
  profile_id: string | null;
  serial_number: string | null;
  box_number: string | null;
  buyer_name: string | null;
  sold_at: string | null;
  board: string | null;
  memory: string | null;
  chip: string | null;
  power_supply: string | null;
  case: string | null;
  display: string | null;
  last_seen_at: string | null;
  created_at: string;
}

export interface MachineDetailsInput {
  name: string;
  serial_number: string | null;
  box_number: string | null;
  buyer_name: string | null;
  sold_at: string | null;
  board: string | null;
  memory: string | null;
  chip: string | null;
  power_supply: string | null;
  case: string | null;
  display: string | null;
}

export interface CreateMachineInput extends MachineDetailsInput {
  certificate_pem: string; // TPM-backed certificate generated by the box
}

export interface AuditEvent {
  id: string;
  machine_id: string | null;
  machine_name: string | null;
  admin_user_id: string | null;
  admin_username: string | null;
  action: string;
  detail: Record<string, unknown>;
  created_at: string;
}

export type AssetStatus = "pending" | "downloading" | "downloaded" | "failed";

export interface ReleaseAsset {
  id: string;
  name: string;
  size_bytes: number;
  status: AssetStatus;
  sha256: string | null;
  downloaded_at: string | null;
  download_error: string | null;
}

export interface Release {
  id: string;
  repo: string;
  tag_name: string;
  name: string;
  published_at: string | null;
  enabled: boolean;
  assets: ReleaseAsset[];
  profile_ids: string[];
}

export interface ImagePull {
  image_name: string;
  tag: string;
  pull_count: number;
  first_pulled_at: string;
  last_pulled_at: string;
}

export interface Profile {
  id: string;
  slug: string;
  name: string;
  created_at: string;
  release_ids: string[];
}

export type MachineSubscriptionStatus = "active" | "expired" | "cancelled" | "revoked";
export type SubscriptionAction = "subscribe" | "cancel" | "renew" | "revoke" | "auto_end";

export interface Subscription {
  id: string;
  slug: string;
  name: string;
  created_at: string;
  subscriber_count: number;
  last_updated: string | null;
}

export interface Subscriber {
  id: string;
  machine_id: string;
  machine_name: string;
  status: MachineSubscriptionStatus;
  started_at: string;
  expires_at: string | null;
  ended_at: string | null;
  updated_at: string;
  service_state: Record<string, unknown>;
  available_service_actions: string[];
}

export interface MachineSubscription {
  id: string;
  subscription_id: string;
  slug: string;
  status: MachineSubscriptionStatus;
  started_at: string;
  expires_at: string | null;
  ended_at: string | null;
  service_state: Record<string, unknown>;
  available_service_actions: string[];
}

export interface VpnDomain {
  prefix: string | null;
  domain: string | null;
  applied: boolean;
}

export interface MachineSubscriptionEvent {
  id: string;
  event_type: string;
  actor_type: string;
  detail: Record<string, unknown>;
  occurred_at: string;
}

export interface CatalogueItem {
  id: string;
  name: string;
  filename: string;
  content_type: string | null;
  size_bytes: number;
  sha256: string;
  created_at: string;
}

export interface HardwareOption {
  value: string;
  count: number;
}

export interface HardwareOptions {
  board: HardwareOption[];
  memory: HardwareOption[];
  chip: HardwareOption[];
  power_supply: HardwareOption[];
  case: HardwareOption[];
  display: HardwareOption[];
}

export const api = {
  login: (username: string, password: string) =>
    request<{ token: string }>("/admin/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    }),

  listMachines: () => request<Machine[]>("/admin/machines"),
  createMachine: (details: CreateMachineInput) =>
    request<Machine>("/admin/machines", {
      method: "POST",
      body: JSON.stringify(details),
    }),
  updateMachineDetails: (id: string, details: MachineDetailsInput) =>
    request<Machine>(`/admin/machines/${id}`, {
      method: "PATCH",
      body: JSON.stringify(details),
    }),
  deleteMachine: (id: string) =>
    request<void>(`/admin/machines/${id}`, { method: "DELETE" }),
  setMachineProfile: (id: string, profileId: string | null) =>
    request<Machine>(`/admin/machines/${id}/profile`, {
      method: "PATCH",
      body: JSON.stringify({ profile_id: profileId }),
    }),
  getHardwareOptions: () =>
    request<HardwareOptions>("/admin/machines/hardware-options"),

  listAuditEvents: (machineId?: string) =>
    request<AuditEvent[]>(
      `/admin/audit${machineId ? `?machine_id=${machineId}` : ""}`,
    ),

  listReleases: () => request<Release[]>("/admin/releases"),
  syncReleases: (repo: string) =>
    request<{ synced: number }>(`/admin/releases/sync?repo=${encodeURIComponent(repo)}`, {
      method: "POST",
    }),
  setReleaseEnabled: (id: string, enabled: boolean) =>
    request<Release>(`/admin/releases/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ enabled }),
    }),
  downloadAsset: (releaseId: string, assetId: string) =>
    request<ReleaseAsset>(`/admin/releases/${releaseId}/assets/${assetId}/download`, {
      method: "POST",
    }),

  listProfiles: () => request<Profile[]>("/admin/profiles"),
  createProfile: (slug: string, name: string) =>
    request<Profile>("/admin/profiles", {
      method: "POST",
      body: JSON.stringify({ slug, name }),
    }),
  deleteProfile: (id: string) =>
    request<void>(`/admin/profiles/${id}`, { method: "DELETE" }),
  assignReleaseToProfile: (profileId: string, releaseId: string) =>
    request<Profile>(`/admin/profiles/${profileId}/releases/${releaseId}`, {
      method: "POST",
    }),
  unassignReleaseFromProfile: (profileId: string, releaseId: string) =>
    request<Profile>(`/admin/profiles/${profileId}/releases/${releaseId}`, {
      method: "DELETE",
    }),

  listSubscriptions: () => request<Subscription[]>("/admin/subscriptions"),
  listSubscribers: (subscriptionId: string) =>
    request<Subscriber[]>(`/admin/subscriptions/${subscriptionId}/machines`),
  listMachineSubscriptions: (machineId: string) =>
    request<MachineSubscription[]>(`/admin/machines/${machineId}/subscriptions`),
  subscribeMachine: (
    machineId: string,
    subscriptionId: string,
    expiresAt: string | null,
  ) =>
    api.runSubscriptionAction(machineId, subscriptionId, "subscribe", expiresAt),
  runSubscriptionAction: (
    machineId: string,
    subscriptionId: string,
    action: SubscriptionAction,
    expiresAt: string | null = null,
  ) =>
    request<MachineSubscription>(
      `/admin/machines/${machineId}/subscriptions/${subscriptionId}/${action}`,
      {
        method: "POST",
        body: JSON.stringify({ expires_at: expiresAt }),
      },
    ),
  listMachineSubscriptionEvents: (
    machineId: string,
    subscriptionId: string,
    machineSubscriptionId: string,
  ) =>
    request<MachineSubscriptionEvent[]>(
      `/admin/machines/${machineId}/subscriptions/${subscriptionId}/periods/${machineSubscriptionId}/events`,
    ),
  runSubscriptionServiceAction: (
    machineId: string,
    subscriptionId: string,
    machineSubscriptionId: string,
    actionName: string,
  ) =>
    request<MachineSubscription>(
      `/admin/machines/${machineId}/subscriptions/${subscriptionId}/periods/${machineSubscriptionId}/service-actions/${actionName}`,
      { method: "POST" },
    ),

  getVpnDomain: (machineId: string) =>
    request<VpnDomain>(`/admin/machines/${machineId}/vpn/domain`),
  setVpnDomain: (machineId: string, prefix: string) =>
    request<VpnDomain>(`/admin/machines/${machineId}/vpn/domain`, {
      method: "PUT",
      body: JSON.stringify({ prefix }),
    }),
  clearVpnDomain: (machineId: string) =>
    request<VpnDomain>(`/admin/machines/${machineId}/vpn/domain`, { method: "DELETE" }),

  listCatalogueItems: () => request<CatalogueItem[]>("/admin/catalogue/items"),
  uploadCatalogueItem: async (name: string, file: File) => {
    const token = getToken();
    const params = new URLSearchParams({ name, filename: file.name });
    const response = await fetch(`/admin/catalogue/items?${params.toString()}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/octet-stream",
        ...(file.type ? { "X-File-Content-Type": file.type } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: file,
    });

    if (!response.ok) {
      if (response.status === 401) clearToken();
      const body = await response.json().catch(() => ({ detail: response.statusText }));
      throw new ApiError(response.status, body.detail ?? response.statusText);
    }

    return response.json() as Promise<CatalogueItem>;
  },

  listImagePulls: () => request<ImagePull[]>("/admin/image-pulls"),
};
